import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { countryData } from '../data/countryData';
import { heatmapApi, HeatmapPoint, ViewportBounds } from '../services/heatmapApi';
import { companyApi } from '../services/companyApi';
import { Company } from '../types/CompanyTypes';
import { macroeconomicApi, MacroeconomicData } from '../services/macroeconomicApi';
import HeatmapControls from './HeatmapControls';
// import CompanyMapLayer from './CompanyMapLayer';
import '../config/mapbox.config'; // Import to set the access token
import './SalaryMap.css';

// Mapbox access token is now set in mapbox.config.ts

interface SalaryMapProps {
  onCountrySelect: (country: any) => void;
  onCompanySelect?: (company: Company) => void;
  darkMode: boolean;
  heatmapMode?: boolean;
  dataType?: 'jobs' | 'salaries' | 'companies';
  onDataTypeChange?: (dataType: 'jobs' | 'salaries' | 'companies') => void;
  onLoginRequired?: (action: 'apply' | 'save' | 'share') => void;
}

const SalaryMap: React.FC<SalaryMapProps> = ({ 
  onCountrySelect,
  onCompanySelect, 
  darkMode, 
  heatmapMode = true,
  dataType = 'salaries',
  onDataTypeChange,
  onLoginRequired
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [currentBounds, setCurrentBounds] = useState<ViewportBounds | null>(null);
  const [heatmapData, setHeatmapData] = useState<HeatmapPoint[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCompanies, setShowCompanies] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [macroData, setMacroData] = useState<{ [key: string]: MacroeconomicData }>({});
  const [macroLoading, setMacroLoading] = useState<boolean>(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Load macroeconomic data on component mount
  useEffect(() => {
    const loadMacroeconomicData = async () => {
      try {
        console.log('🌍 Loading macroeconomic data from local database...');
        const dataMap = await macroeconomicApi.getMacroeconomicDataMap(2022);
        
        setMacroData(dataMap);
        console.log('✅ Loaded macroeconomic data for', Object.keys(dataMap).length, 'countries');
      } catch (error) {
        console.error('❌ Failed to load macroeconomic data:', error);
      } finally {
        setMacroLoading(false);
      }
    };

    loadMacroeconomicData();
  }, []);

  // Load viewport data
  const loadViewportData = useCallback(async (bounds: ViewportBounds) => {
    if (!bounds) return;
    
    setLoading(true);
    try {
      // Load heatmap data
      const heatmapResponse = await heatmapApi.getHeatmapData(bounds, dataType);
      setHeatmapData(heatmapResponse.data || []);
      
      // Load company data if enabled
      if (showCompanies) {
        try {
          const companyResponse = await companyApi.getCompaniesInViewport(
            { north: bounds.north, south: bounds.south, east: bounds.east, west: bounds.west },
            bounds.zoom
          );
          // Type mismatch - need to map the company data
          setCompanies(companyResponse.companies as any);
          console.log(`🏢 Loaded ${companyResponse.count} companies`);
        } catch (error) {
          console.error('Error loading company data:', error);
          setCompanies([]);
        }
      }
      
      // Update heatmap layer
      if (map.current && map.current.isStyleLoaded()) {
        const source = map.current.getSource('viewport-heatmap');
        if (source && source.type === 'geojson') {
          const geojsonData = {
            type: 'FeatureCollection',
            features: heatmapResponse.data?.map(point => ({
              type: 'Feature',
              properties: {
                value: point.value,
                type: point.type,
                id: point.id
              },
              geometry: {
                type: 'Point',
                coordinates: point.coordinates
              }
            })) || []
          };
          (source as mapboxgl.GeoJSONSource).setData(geojsonData as any);
        }
      }
      
      console.log(`📊 Loaded ${heatmapResponse.count} points`);
    } catch (error) {
      console.error('Error loading viewport data:', error);
      // Fallback to mock data
      const mockData = generateMockHeatmapData(bounds, dataType);
      setHeatmapData(mockData);
    } finally {
      setLoading(false);
    }
  }, [dataType, showCompanies]);

  // Generate mock heatmap data
  const generateMockHeatmapData = (bounds: ViewportBounds, type: string): HeatmapPoint[] => {
    const points: HeatmapPoint[] = [];
    const density = Math.min(100, Math.max(10, 150 - bounds.zoom * 10));
    
    for (let i = 0; i < density; i++) {
      const lat = bounds.south + Math.random() * (bounds.north - bounds.south);
      const lng = bounds.west + Math.random() * (bounds.east - bounds.west);
      
      points.push({
        id: `${type}-${i}`,
        coordinates: [lng, lat],
        value: Math.random() * 100000 + 30000,
        type: type as 'jobs' | 'salaries' | 'companies',
        metadata: {
          zoom: bounds.zoom
        }
      });
    }
    
    return points;
  };

  // Initialize map only once
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: darkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      center: [10, 40],
      zoom: 2.5
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update map style when dark mode changes
  useEffect(() => {
    if (!map.current || !map.current.loaded()) return;
    map.current.setStyle(darkMode ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11');
  }, [darkMode]);

  // Setup map handlers and initial display
  useEffect(() => {
    if (!map.current) return;

    const setupMap = () => {
      if (!map.current) return;

      // Clear existing markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];

      // Remove existing layers and sources
      const layersToRemove = [
        'viewport-heatmap-layer', 'salary-labels', 'job-labels', 
        'country-fills', 'country-borders', 'country-labels', 'country-hover',
        'region-labels', 'city-labels'
      ];
      
      layersToRemove.forEach(layerId => {
        if (map.current?.getLayer(layerId)) {
          map.current.removeLayer(layerId);
        }
      });
      
      const sourcesToRemove = ['viewport-heatmap', 'countries', 'regions', 'cities'];
      sourcesToRemove.forEach(sourceId => {
        if (map.current?.getSource(sourceId)) {
          map.current.removeSource(sourceId);
        }
      });

      if (heatmapMode) {
        // Setup heatmap source first
        map.current.addSource('viewport-heatmap', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        });

        const colors = {
          jobs: ['rgba(25, 118, 210, 0)', 'rgba(25, 118, 210, 0.6)', 'rgba(13, 71, 161, 1)'],
          salaries: ['rgba(30, 136, 229, 0)', 'rgba(30, 136, 229, 0.6)', 'rgba(21, 101, 192, 1)'],
          companies: ['rgba(13, 71, 161, 0)', 'rgba(13, 71, 161, 0.6)', 'rgba(1, 87, 155, 1)']
        };

        map.current.addLayer({
          id: 'viewport-heatmap-layer',
          type: 'heatmap',
          source: 'viewport-heatmap',
          paint: {
            'heatmap-weight': [
              'interpolate',
              ['linear'],
              ['get', 'value'],
              30000, 0.1,
              150000, 0.6
            ],
            'heatmap-intensity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              2, 0.3,
              5, 0.4,
              8, 0.5,
              12, 0.6
            ],
            'heatmap-color': [
              'interpolate',
              ['linear'],
              ['heatmap-density'],
              0, colors[dataType][0],
              0.2, colors[dataType][1],
              0.8, colors[dataType][2]
            ],
            'heatmap-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              2, 15,
              5, 25,
              8, 35,
              12, 45
            ],
            'heatmap-opacity': [
              'interpolate',
              ['linear'],
              ['zoom'],
              2, 0.3,
              5, 0.4,
              8, 0.5,
              12, 0.6
            ]
          }
        });
        
        // Add country boundaries on top of heatmap
        addCountryBoundaries();

        // Add salary/data labels at higher zoom levels
        if (dataType === 'salaries') {
          map.current.addLayer({
            id: 'salary-labels',
            type: 'symbol',
            source: 'viewport-heatmap',
            minzoom: 8,
            layout: {
              'text-field': [
                'concat',
                '€',
                ['to-string', ['round', ['/', ['get', 'value'], 1000]]],
                'k'
              ],
              'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8, 10,
                12, 14,
                15, 18
              ],
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': false
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 2,
              'text-halo-blur': 1,
              'text-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8, 0.5,
                10, 0.8,
                12, 1
              ]
            }
          });
        } else if (dataType === 'jobs') {
          map.current.addLayer({
            id: 'job-labels',
            type: 'symbol',
            source: 'viewport-heatmap',
            minzoom: 8,
            layout: {
              'text-field': [
                'concat',
                ['to-string', ['get', 'value']],
                ' jobs'
              ],
              'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8, 10,
                12, 14,
                15, 18
              ],
              'text-anchor': 'center',
              'text-allow-overlap': false
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#0064FF',
              'text-halo-width': 2,
              'text-halo-blur': 1,
              'text-opacity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                8, 0.5,
                10, 0.8,
                12, 1
              ]
            }
          });
        }

        // Handle viewport changes with debouncing
        const handleViewportChange = () => {
          if (!map.current) return;
          
          const bounds = map.current.getBounds();
          const zoom = map.current.getZoom();
          
          if (!bounds) return;
          
          const viewportBounds: ViewportBounds = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
            zoom: zoom
          };
          
          setCurrentBounds(viewportBounds);
          
          // Debounce the data loading
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(() => {
            loadViewportData(viewportBounds);
          }, 300);
        };

        map.current.on('moveend', handleViewportChange);
        map.current.on('zoomend', handleViewportChange);
        
        // Load initial data
        handleViewportChange();

        // Cleanup
        return () => {
          if (map.current) {
            map.current.off('moveend', handleViewportChange);
            map.current.off('zoomend', handleViewportChange);
          }
        };
      } else {
        // Add interactive country layer for traditional mode
        addInteractiveCountries();
        // Setup traditional markers
        (countryData || []).forEach(country => {
          const el = document.createElement('div');
          el.className = 'country-marker';
          el.innerHTML = `
            <div class="marker-content">
              <div class="country-flag">${country.flag}</div>
              <div class="salary-range">${country.avgSalaryRange}</div>
            </div>
          `;

          el.addEventListener('mouseenter', () => {
            setHoveredCountry(country.name);
            el.classList.add('hovered');
          });

          el.addEventListener('mouseleave', () => {
            setHoveredCountry(null);
            el.classList.remove('hovered');
          });

          el.addEventListener('click', () => {
            onCountrySelect(country);
          });

          const marker = new mapboxgl.Marker(el)
            .setLngLat(country.coordinates)
            .addTo(map.current!);
            
          markersRef.current.push(marker);
        });
      }
    };

    if (map.current.loaded()) {
      setupMap();
    } else {
      map.current.once('load', setupMap);
    }
  }, [heatmapMode, dataType, loadViewportData, onCountrySelect]);

  // Add country boundaries for context with zoom-responsive details
  const addCountryBoundaries = () => {
    if (!map.current) return;
    
    // Add regional boundaries based on zoom level
    addZoomBasedBoundaries();
    
    // Create GeoJSON from country data
    const countryFeatures = (countryData || []).map(country => ({
      type: 'Feature' as const,
      properties: {
        id: country.id,
        name: country.name,
        avgSalary: country.avgSalary,
        avgSalaryRange: country.avgSalaryRange,
        flag: country.flag,
        totalJobs: country.marketStats.totalJobs,
        techGrowth: country.marketStats.techJobGrowth,
        workLifeBalance: country.marketStats.workLifeBalance,
        costOfLiving: country.costOfLivingIndex,
        currency: country.economicIndicators.currencySymbol
      },
      geometry: {
        type: 'Point' as const,
        coordinates: country.coordinates as [number, number]
      }
    }));

    map.current.addSource('countries', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection' as const,
        features: countryFeatures
      }
    });

    // Add country fill layer with subtle circles (not squares)
    map.current.addLayer({
      id: 'country-fills',
      type: 'circle',
      source: 'countries',
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2, 30,
          4, 50,
          6, 80,
          8, 120
        ],
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'avgSalary'],
          20000, '#fee5d9',
          40000, '#fcbba1', 
          60000, '#fc9272',
          80000, '#fb6a4a',
          100000, '#de2d26'
        ],
        'circle-opacity': 0.15,
        'circle-blur': 0.8,
        'circle-stroke-width': 0,
        'circle-stroke-opacity': 0
      }
    });

    // Add country labels with salary info
    map.current.addLayer({
      id: 'country-labels',
      type: 'symbol',
      source: 'countries',
      minzoom: 3,
      layout: {
        'text-field': [
          'format',
          ['get', 'flag'], {},
          '\n', {},
          ['get', 'name'], { 'font-scale': 0.8 },
          '\n', {},
          ['get', 'avgSalaryRange'], { 'font-scale': 0.7 }
        ],
        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          3, 12,
          6, 16,
          9, 20
        ],
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-ignore-placement': false
      },
      paint: {
        'text-color': darkMode ? '#ffffff' : '#333333',
        'text-halo-color': darkMode ? '#000000' : '#ffffff',
        'text-halo-width': 2,
        'text-halo-blur': 1
      }
    });

    // Add interactive features
    addCountryInteractions();
  };

  // Add fully interactive countries for traditional mode
  const addInteractiveCountries = () => {
    if (!map.current) return;
    
    addCountryBoundaries();
    
    // Country fills disabled to avoid visual clutter
    // if (map.current.getLayer('country-fills')) {
    //   map.current.setPaintProperty('country-fills', 'circle-opacity', 0.4);
    //   map.current.setPaintProperty('country-fills', 'circle-stroke-opacity', 0);
    // }
  };

  // Add country interaction handlers
  const addCountryInteractions = () => {
    if (!map.current) return;

    // Country hover effect
    map.current.on('mouseenter', 'country-fills', (e) => {
      if (!map.current || !e.features?.[0]) return;
      
      map.current.getCanvas().style.cursor = 'pointer';
      
      const feature = e.features[0];
      if (!feature.properties || feature.geometry.type !== 'Point') return;
      
      const coords = feature.geometry.coordinates.slice() as [number, number];
      
      // Create hover popup
      const country = (countryData || []).find(c => c.id === feature.properties?.id);
      if (!country) return;
      
      // Get macroeconomic data for this country
      const macro = macroData[country.id];
      
      const popupContent = `
        <div class="country-hover-popup ${darkMode ? 'dark' : ''}">
          <div class="popup-header">
            <span class="country-flag">${country.flag}</span>
            <h3>${country.name}</h3>
          </div>
          
          <div class="popup-stats">
            <div class="stat-section">
              <h4 class="section-title"><span class="material-icons">work</span> Job Market</h4>
              <div class="stat-row">
                <span class="stat-label">Avg Salary:</span>
                <span class="stat-value">${country.avgSalaryRange}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Tech Jobs:</span>
                <span class="stat-value">${country.marketStats.totalJobs.toLocaleString()}</span>
              </div>
              <div class="stat-row">
                <span class="stat-label">Growth:</span>
                <span class="stat-value">+${country.marketStats.techJobGrowth}%</span>
              </div>
              ${macro?.unemploymentRate ? `
              <div class="stat-row">
                <span class="stat-label">Unemployment:</span>
                <span class="stat-value">${macro.unemploymentRate.toFixed(1)}%</span>
              </div>
              ` : ''}
            </div>
            
            <div class="stat-section">
              <h4 class="section-title"><span class="material-icons">account_balance</span> Economy</h4>
              ${macro?.gdpPerCapita ? `
              <div class="stat-row">
                <span class="stat-label">GDP per capita:</span>
                <span class="stat-value">$${(macro.gdpPerCapita / 1000).toFixed(1)}k</span>
              </div>
              ` : ''}
              ${macro?.inflationRate ? `
              <div class="stat-row">
                <span class="stat-label">Inflation:</span>
                <span class="stat-value">${macro.inflationRate > 0 ? '+' : ''}${macro.inflationRate.toFixed(1)}%</span>
              </div>
              ` : ''}
              <div class="stat-row">
                <span class="stat-label">Cost of Living:</span>
                <span class="stat-value">${country.costOfLivingIndex}% vs EU avg</span>
              </div>
            </div>
            
            <div class="stat-section">
              <h4 class="section-title"><span class="material-icons">home</span> Quality of Life</h4>
              <div class="stat-row">
                <span class="stat-label">Work-Life:</span>
                <span class="stat-value">${country.marketStats.workLifeBalance}/10</span>
              </div>
              ${macro?.urbanPopulation ? `
              <div class="stat-row">
                <span class="stat-label">Urban Population:</span>
                <span class="stat-value">${macro.urbanPopulation.toFixed(1)}%</span>
              </div>
              ` : ''}
              ${macro?.internetUsers ? `
              <div class="stat-row">
                <span class="stat-label">Internet Users:</span>
                <span class="stat-value">${macro.internetUsers.toFixed(1)}%</span>
              </div>
              ` : ''}
            </div>
          </div>
          
          <div class="popup-footer">
            <small>Click for detailed breakdown • Data: World Bank ${macro ? macro.year : '2022'}</small>
          </div>
        </div>
      `;
      
      // Add hover layer
      if (!map.current.getLayer('country-hover')) {
        map.current.addLayer({
          id: 'country-hover',
          type: 'circle',
          source: 'countries',
          paint: {
            'circle-radius': [
              'interpolate',
              ['linear'],
              ['zoom'],
              2, 35,
              4, 55,
              6, 85,
              8, 125
            ],
            'circle-color': '#ffffff',
            'circle-opacity': 0,
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ff6b35',
            'circle-stroke-opacity': 0.8
          },
          filter: ['==', 'id', '']
        });
      }
      
      map.current.setFilter('country-hover', ['==', 'id', feature.properties?.id || '']);
      
      new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: 'country-popup-container'
      })
        .setLngLat(coords)
        .setHTML(popupContent)
        .addTo(map.current);
    });

    map.current.on('mouseleave', 'country-fills', () => {
      if (!map.current) return;
      
      map.current.getCanvas().style.cursor = '';
      map.current.setFilter('country-hover', ['==', 'id', '']);
      
      // Remove popup
      const popup = document.querySelector('.mapboxgl-popup');
      if (popup) popup.remove();
    });

    // Country click handler
    map.current.on('click', 'country-fills', (e) => {
      if (!e.features?.[0]) return;
      
      const feature = e.features[0];
      if (!feature.properties) return;
      
      const country = (countryData || []).find(c => c.id === feature.properties?.id);
      
      if (country) {
        onCountrySelect(country);
        
        // Zoom to country
        map.current?.flyTo({
          center: country.coordinates,
          zoom: 6,
          duration: 1500
        });
      }
    });
  };

  // Update heatmap when data type changes
  useEffect(() => {
    if (heatmapMode && currentBounds) {
      loadViewportData(currentBounds);
    }
  }, [dataType, heatmapMode, currentBounds, loadViewportData]);

  // Add zoom-based regional boundaries
  const addZoomBasedBoundaries = () => {
    if (!map.current) return;
    
    const currentZoom = map.current.getZoom();
    const bounds = map.current.getBounds();
    
    if (!bounds) return;
    
    // Load different boundary details based on zoom level
    if (currentZoom >= 6 && currentZoom < 9) {
      // Regional/State level (zoom 6-8)
      loadRegionalBoundaries(bounds);
    } else if (currentZoom >= 9) {
      // City level (zoom 9+) 
      loadCityBoundaries(bounds);
    }
  };

  // Load regional/state boundaries
  const loadRegionalBoundaries = async (bounds: mapboxgl.LngLatBounds) => {
    if (!map.current) return;
    
    try {
      const regionsInView = getRegionsInBounds(bounds);
      
      if (regionsInView && regionsInView.length > 0 && !map.current.getSource('regions')) {
        const regionFeatures = regionsInView.map(region => ({
          type: 'Feature' as const,
          properties: {
            id: region.id,
            name: region.name,
            avgSalary: region.avgSalary,
            jobCount: region.jobCount,
            type: 'region'
          },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [region.boundary]
          }
        }));

        map.current.addSource('regions', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection' as const,
            features: regionFeatures
          }
        });

        // Disable region fill layer to avoid squares
        // map.current.addLayer({
        //   id: 'region-fills',
        //   type: 'fill',
        //   source: 'regions',
        //   paint: {
        //     'fill-color': [
        //       'interpolate',
        //       ['linear'],
        //       ['get', 'avgSalary'],
        //       30000, '#E3F2FD',
        //       50000, '#90CAF9',
        //       70000, '#42A5F5',
        //       100000, '#1976D2'
        //     ],
        //     'fill-opacity': 0.3,
        //     'fill-outline-color': '#1565C0'
        //   }
        // });
      }
    } catch (error) {
      console.log('Regional boundaries not available for this area');
    }
  };

  // Load city boundaries  
  const loadCityBoundaries = async (bounds: mapboxgl.LngLatBounds) => {
    if (!map.current) return;
    
    try {
      const citiesInView = getCitiesInBounds(bounds);
      
      if (citiesInView && citiesInView.length > 0 && !map.current.getSource('cities')) {
        const cityFeatures = citiesInView.map(city => ({
          type: 'Feature' as const,
          properties: {
            id: city.id,
            name: city.name,
            avgSalary: city.avgSalary,
            jobCount: city.jobCount,
            type: 'city'
          },
          geometry: {
            type: 'Point' as const,
            coordinates: city.coordinates as [number, number]
          }
        }));

        map.current.addSource('cities', {
          type: 'geojson',
          data: {
            type: 'FeatureCollection' as const,
            features: cityFeatures
          }
        });

        // Disable city circles to avoid visual clutter
        // map.current.addLayer({
        //   id: 'city-fills',
        //   type: 'circle',
        //   source: 'cities',
        //   paint: {
        //     'circle-radius': [
        //       'interpolate',
        //       ['linear'],
        //       ['get', 'jobCount'],
        //       10, 12,
        //       500, 30,
        //       1500, 50
        //     ],
        //     'circle-color': [
        //       'interpolate',
        //       ['linear'],
        //       ['get', 'avgSalary'],
        //       30000, '#E3F2FD',
        //       50000, '#90CAF9', 
        //       70000, '#42A5F5',
        //       100000, '#1976D2'
        //     ],
        //     'circle-opacity': 0.5,
        //     'circle-blur': 0.5,
        //     'circle-stroke-width': 1,
        //     'circle-stroke-color': '#1565C0'
        //   }
        // });
      }
    } catch (error) {
      console.log('City boundaries not available for this area');
    }
  };

  // Get regions within bounds
  const getRegionsInBounds = (bounds: mapboxgl.LngLatBounds) => {
    const europeanRegions = [
      {
        id: 'bavaria',
        name: 'Bavaria',
        avgSalary: 65000,
        jobCount: 1500,
        boundary: [
          [9.0, 47.0], [13.8, 47.0], [13.8, 50.5], [9.0, 50.5], [9.0, 47.0]
        ]
      },
      {
        id: 'north-rhine-westphalia', 
        name: 'North Rhine-Westphalia',
        avgSalary: 62000,
        jobCount: 2200,
        boundary: [
          [5.8, 50.3], [9.5, 50.3], [9.5, 52.5], [5.8, 52.5], [5.8, 50.3]
        ]
      },
      {
        id: 'ile-de-france',
        name: 'Île-de-France', 
        avgSalary: 75000,
        jobCount: 3500,
        boundary: [
          [1.4, 48.1], [3.6, 48.1], [3.6, 49.2], [1.4, 49.2], [1.4, 48.1]
        ]
      }
    ];

    return europeanRegions.filter(region => {
      const [minLng, minLat] = region.boundary[0];
      const [maxLng, maxLat] = region.boundary[2];
      
      // Check if region boundary intersects with current view bounds
      const regionBounds = new mapboxgl.LngLatBounds([minLng, minLat], [maxLng, maxLat]);
      
      return (
        bounds.getNorth() >= regionBounds.getSouth() &&
        bounds.getSouth() <= regionBounds.getNorth() &&
        bounds.getEast() >= regionBounds.getWest() &&
        bounds.getWest() <= regionBounds.getEast()
      );
    });
  };

  // Get cities within bounds
  const getCitiesInBounds = (bounds: mapboxgl.LngLatBounds) => {
    const techCities = [
      { id: 'berlin', name: 'Berlin', coordinates: [13.4050, 52.5200], avgSalary: 68000, jobCount: 850 },
      { id: 'munich', name: 'Munich', coordinates: [11.5820, 48.1351], avgSalary: 75000, jobCount: 650 },
      { id: 'amsterdam', name: 'Amsterdam', coordinates: [4.9041, 52.3676], avgSalary: 72000, jobCount: 720 },
      { id: 'paris', name: 'Paris', coordinates: [2.3522, 48.8566], avgSalary: 78000, jobCount: 920 },
      { id: 'london', name: 'London', coordinates: [-0.1276, 51.5074], avgSalary: 85000, jobCount: 1200 },
      { id: 'zurich', name: 'Zurich', coordinates: [8.5417, 47.3769], avgSalary: 95000, jobCount: 450 },
      { id: 'stockholm', name: 'Stockholm', coordinates: [18.0686, 59.3293], avgSalary: 82000, jobCount: 580 },
      { id: 'dublin', name: 'Dublin', coordinates: [-6.2603, 53.3498], avgSalary: 88000, jobCount: 620 }
    ];

    return techCities.filter(city => 
      bounds.contains(new mapboxgl.LngLat(city.coordinates[0], city.coordinates[1]))
    );
  };

  return (
    <div className="salary-map-container">
      <div ref={mapContainer} className="salary-map" style={{ width: '100%', height: '100%' }} />
      
      {/* Loading indicator */}
      {(loading || macroLoading) && (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <span>{macroLoading ? 'Loading economic data...' : 'Loading data...'}</span>
        </div>
      )}
      
      {/* Modern heatmap controls */}
      {heatmapMode && (
        <HeatmapControls
          dataType={dataType}
          onDataTypeChange={(type) => onDataTypeChange?.(type)}
          currentBounds={currentBounds}
          dataPointsCount={heatmapData?.length || 0}
          darkMode={darkMode}
          showCompanies={showCompanies}
          onToggleCompanies={setShowCompanies}
          companyCount={companies?.length || 0}
          macroDataCount={macroData ? Object.keys(macroData).length : 0}
        />
      )}

      {/* Company layer */}
      {/* <CompanyMapLayer
        map={map.current}
        companies={companies}
        visible={heatmapMode && showCompanies}
        darkMode={darkMode}
        onCompanyClick={(company) => {
          console.log('SalaryMapFixed - Company clicked:', company);
          console.log('SalaryMapFixed - onCompanySelect function exists:', !!onCompanySelect);
          if (onCompanySelect) {
            console.log('SalaryMapFixed - Calling onCompanySelect');
            onCompanySelect(company);
          }
        }}
      /> */}
      
      {/* Traditional tooltip */}
      {hoveredCountry && !heatmapMode && (
        <div className="hover-tooltip">
          <strong>{hoveredCountry}</strong>
          <p>Click to calculate take-home salary</p>
        </div>
      )}
    </div>
  );
};

export default SalaryMap;